import { BaseTable, Checkbox, Loading, Select } from "components";
import { DataListType } from "../../types";
import moment from "moment";
import { Controller, useFormContext, UseFormReturn, useWatch } from "react-hook-form";
import { FormType } from "../../schemas";
import { handleCheckAllDate, mapingDate } from "../../functions";
import { useLocation } from "utils";
import { useShift } from "../../hooks";
import { Update } from "./Update";

type Props = {
    data: DataListType[]
    isLoading: boolean
    start_date: string
    end_date: string
    method: UseFormReturn<FormType>
    isEdit?: boolean
    refecth?: () => void
}

export function Table({
    data,
    isLoading,
    start_date,
    end_date,
    method,
    isEdit,
    refecth
}: Props) {

    // useForm
    const { watch, setValue, control } = useFormContext<FormType>()
    const detail = useWatch({ control, name: "detail" })

    // hooks 
    const { data: location, isLoading: isLoadingLocation } = useLocation()
    const { data: shift, isLoading: isLoadingShift } = useShift()

    // function
    const listDate = mapingDate(start_date, end_date);


    return (
        <div className="overflow-auto max-h-[calc(100svh-620px)]">
            <BaseTable className="border-collapse w-full">
                <thead className="sticky top-20 p-1 z-[1] !bg-black !text-white">
                    <tr>
                        <th rowSpan={2} className="sticky left-0 z-[1] bg-black w-[60px]">
                            NO
                        </th>
                        <th rowSpan={2} className="sticky left-[60px] z-[1] bg-black w-[100px]">
                            CODE
                        </th>
                        <th rowSpan={2} className="sticky left-[160px] z-[1] bg-black w-[180px]">
                            EMPLOYEE
                        </th>
                        <th rowSpan={2}>POSITION</th>
                        <th rowSpan={2}>DIVISI</th>
                        <th rowSpan={2}>DEPARTEMENT</th>
                        <th rowSpan={2}>LOCATION</th>
                        <th colSpan={listDate.length}>SCHEDULE</th>
                        {isEdit &&
                            <th rowSpan={2}>ACTION</th>
                        }
                    </tr>
                    <tr>
                        {listDate.map((date, dateIndex) => (
                            <th key={dateIndex}>
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        value={detail?.every(item => item.shift_schedule?.[dateIndex]?.checked)}
                                        onChange={(e) => {
                                            handleCheckAllDate(method, detail, {
                                                dateIndex,
                                                checked: e.target.checked
                                            })
                                        }}
                                    />
                                    {moment(date).format("DD/MM/YYYY")}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {isLoading && data.length === 0 && (
                        <tr>
                            <td colSpan={9} className="text-center">
                                <Loading loading={isLoading} errorText="No data available" />
                            </td>
                        </tr>
                    )}

                    {data.map((item, rowIndex) => (
                        <tr key={rowIndex}>
                            <td className="sticky top-[95px] left-0 z-[1] bg-white w-[60px]">
                                {rowIndex + 1}
                            </td>

                           <td className="sticky top-[95px] left-[60px] z-[1] bg-white w-[100px]">
                                {item.employee_code}
                            </td>

                            <td className="sticky top-[95px] left-[160px] z-[1] bg-white w-[180px]">
                                {item.employee_name}
                            </td>

                            <td>{item.job_position_name}</td>
                            <td>{item.divisi_name}</td>
                            <td>{item.department_name}</td>
                            <td>
                                <div className="flex items-center gap-2">
                                    <Controller
                                        name={`detail.${rowIndex}.location_id`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                options={location}
                                                value={field.value ? location.find(opt => opt.value === field.value) : null}
                                                onChange={(opt) => field.onChange(opt?.value)}
                                                isLoading={isLoadingLocation}
                                                className="w-[120px]"
                                            />
                                        )}
                                    />
                                </div>
                            </td>

                            {item?.shift_schedule?.map((subItem, dateIndex) => (
                                <td key={dateIndex}>
                                    <div className="flex items-center gap-2">

                                        <Controller
                                            name={`detail.${rowIndex}.shift_schedule.${dateIndex}.checked`}
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    value={field.value}
                                                    onChange={(val) => {
                                                        const isCheked = field.value;
                                                        field.onChange(val)

                                                        if (!isCheked) {
                                                            setValue(
                                                                `detail.${rowIndex}.shift_schedule.${dateIndex}.mr_shift_id`,
                                                                null,
                                                                {
                                                                    shouldDirty: true,
                                                                    shouldValidate: true,
                                                                    shouldTouch: true,
                                                                }
                                                            )
                                                        }

                                                    }}
                                                />
                                            )}
                                        />

                                        <Controller
                                            name={`detail.${rowIndex}.shift_schedule.${dateIndex}.mr_shift_id`}
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    className="w-[150px]"
                                                    options={shift}
                                                    isClearable
                                                    value={
                                                        field.value
                                                            ? shift.find(opt => opt.value === field.value)
                                                            : null
                                                    }
                                                    onChange={(opt) => {
                                                        field.onChange(opt?.value)

                                                        const selectedShift = shift.find(s => s.value === opt?.value);
                                                        const inTime = selectedShift?.in_time ?? "";
                                                        const outTime = selectedShift?.out_time ?? "";

                                                        setValue(`detail.${rowIndex}.shift_schedule.${dateIndex}.in_time`, inTime);
                                                        setValue(`detail.${rowIndex}.shift_schedule.${dateIndex}.out_time`, outTime);
                                                        setValue(`detail.${rowIndex}.employee_id`, item.employee_id?.toString());
                                                        setValue(`detail.${rowIndex}.divisi_id`, item.divisi_id?.toString());
                                                        setValue(`detail.${rowIndex}.shift_schedule.${dateIndex}.shift_date`, subItem.shift_date);
                                                    }}
                                                    isDisabled={!watch(`detail.${rowIndex}.shift_schedule.${dateIndex}.checked`)}
                                                    isLoading={isLoadingShift}
                                                />
                                            )}
                                        />
                                    </div>
                                </td>
                            ))}

                            {isEdit && (
                                <td>
                                    <Update
                                        rowIndex={rowIndex}
                                        refecth={refecth}
                                    />
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>

            </BaseTable>
        </div>


    )
}